import React, { useCallback, useMemo } from 'react';
import { AppButton, Box, FiservCardInterface, FiservCardStatusEnum } from '@common-fe/common-fe';

import { useShouldShowCardActions } from '../hooks/useShouldShowCardActions';
import useCardActivationModalStore from '../store/useCardActivationModal.store';
import { useSetCardStatusModal } from '../store/useSetCardStatusModal.store';

import { useRequestedActivationCards } from './CardActivationModals/hooks/useRequestedActivationCards';
import DisableActionHintWrapper from './DisableActionHintWrapper';

interface Props {
  card: FiservCardInterface;
  isLoading?: boolean;
  isDarkMode?: boolean;
}

export const CardItemActions: React.FC<Props> = ({ card, isLoading = false, isDarkMode }) => {
  const { status, id } = card;
  const { readyForActivation, disableManage } = useShouldShowCardActions(card);
  const showManage = useMemo(() => {
    return (
      status === FiservCardStatusEnum.ACTIVE ||
      status === FiservCardStatusEnum.PENDING_OPEN ||
      status === FiservCardStatusEnum.PENDING_UNLOCK ||
      status === FiservCardStatusEnum.FROZEN ||
      status === FiservCardStatusEnum.PENDING_FROZEN
    );
  }, [status]);
  const showActive = useMemo(() => status === FiservCardStatusEnum.PENDING_OPEN, [status]);

  const { handleChangeState: cardStatusChangeState } = useSetCardStatusModal();

  const {
    handleChangeVisibility: handleChangeActivationVisibility,
    handleChangeCardID: handleChangeActivationCardID,
    handleChangeLast4Numbers,
    handleChangeAlternateAccountId,
    handleChangeMemberSequenceIdentifier,
    handleChangeTokenCardId,
  } = useCardActivationModalStore();

  const setCardActivationInfo = useCallback(() => {
    handleChangeActivationCardID(id);
    handleChangeLast4Numbers(card.last4Number);
    handleChangeAlternateAccountId(card.alternateAccountId);
    handleChangeMemberSequenceIdentifier(card.memberSequenceIdentifier);
    if (card.tokenCardId) handleChangeTokenCardId(card.tokenCardId);
  }, [
    handleChangeActivationCardID,
    id,
    handleChangeLast4Numbers,
    card,
    handleChangeMemberSequenceIdentifier,
    handleChangeAlternateAccountId,
    handleChangeTokenCardId,
  ]);

  const onCardActivationModal = useCallback(() => {
    setCardActivationInfo();
    handleChangeActivationVisibility(true);
  }, [handleChangeActivationVisibility, setCardActivationInfo]);
  const { isCardRequestedToActivate } = useRequestedActivationCards({
    cardId: id,
    cardStatus: status,
    last4: card.last4Number,
  });

  if (!card) {
    return null;
  }
  return (
    <Box direction="row" justify="between">
      {showActive && (
        <Box margin={{ right: 'spacing12' }} flex>
          <DisableActionHintWrapper
            hideHint={(readyForActivation && !isCardRequestedToActivate(id)) || isLoading}
            hintElement={
              <AppButton
                width="100%"
                isDarkMode={isDarkMode}
                buttonType={isDarkMode ? 'primary' : 'secondary'}
                onClick={onCardActivationModal}
                disabled={!readyForActivation || isCardRequestedToActivate(id) || isLoading}
              >
                Activate Card
              </AppButton>
            }
          />
        </Box>
      )}
      {showManage && (
        <Box flex>
          <DisableActionHintWrapper
            hideHint={!disableManage || isLoading}
            hintElement={
              <AppButton
                isDarkMode={isDarkMode}
                buttonType={isDarkMode ? 'primary' : 'secondary'}
                width="100%"
                disabled={disableManage || isLoading}
                onClick={() => {
                  cardStatusChangeState({
                    cardId: card.id,
                  });
                }}
              >
                Manage Card Access
              </AppButton>
            }
          />
        </Box>
      )}
    </Box>
  );
};
